import React, { useState, useEffect, useRef } from 'react';
import { Menu, Bell, Search, User, Settings, LogOut } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';

interface HeaderProps {
    onMenuClick: () => void;
    title: string;
}

const Header: React.FC<HeaderProps> = ({ onMenuClick, title }) => {
    const [showUserMenu, setShowUserMenu] = useState(false);
    const { user, signOut } = useAuth();
    const userMenuRef = useRef<HTMLDivElement>(null);

    const handleSignOut = async () => {
        try {
            await signOut();
        } catch (error) {
            console.error('Error signing out:', error);
        }
    };

    // Close user menu when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
                setShowUserMenu(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    return (
        <header className="bg-white/90 backdrop-blur-md shadow-lg border-b border-white/20 h-16 flex items-center justify-between px-4 lg:px-6 flex-shrink-0">
            <div className="flex items-center">
                <button
                    onClick={onMenuClick}
                    className="lg:hidden p-2 rounded-xl text-gray-400 hover:text-gray-600 hover:bg-white/50 mr-3 transition-all duration-200"
                >
                    <Menu className="h-5 w-5" />
                </button>
                <div>
                    <h1 className="text-xl font-bold gradient-text">{title}</h1>
                    <p className="text-xs text-gray-500">Noor-e-Hamza Islamic College</p>
                </div>
            </div>

            <div className="flex items-center space-x-3">
                {/* Search */}
                <div className="hidden md:block relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                    <input
                        type="text"
                        placeholder="Search students, teachers, staff..."
                        className="pl-10 pr-4 py-2.5 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent w-80 bg-white/80 backdrop-blur-sm transition-all duration-200"
                    />
                </div>

                {/* Notifications */}
                <button className="p-2.5 rounded-xl text-gray-400 hover:text-gray-600 hover:bg-white/50 relative transition-all duration-200 group">
                    <Bell className="h-5 w-5" />
                    <span className="absolute top-1.5 right-1.5 h-2 w-2 bg-gradient-to-r from-red-500 to-red-600 rounded-full animate-pulse"></span>
                </button>

                {/* User Menu */}
                <div className="relative" ref={userMenuRef}>
                    <button
                        onClick={() => setShowUserMenu(!showUserMenu)}
                        className="flex items-center space-x-2 p-2 rounded-xl hover:bg-white/50 transition-all duration-200 group"
                    >
                        <div className="w-8 h-8 bg-gradient-to-r from-emerald-500 to-teal-600 rounded-full flex items-center justify-center">
                            <User className="h-4 w-4 text-white" />
                        </div>
                        <div className="hidden md:block text-left">
                            <p className="text-sm font-medium text-gray-900">{user?.displayName || 'Admin'}</p>
                            <p className="text-xs text-gray-500 capitalize">{user?.role || 'Admin'}</p>
                        </div>
                    </button>

                    {/* Dropdown Menu */}
                    {showUserMenu && (
                        <div className="absolute right-0 mt-2 w-48 bg-white/90 backdrop-blur-md rounded-xl shadow-xl border border-white/20 py-2 z-50">
                            <div className="px-4 py-2 border-b border-gray-200">
                                <p className="text-sm font-medium text-gray-900">{user?.displayName || 'Admin'}</p>
                                <p className="text-xs text-gray-500">{user?.email}</p>
                            </div>
                            <button className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-white/50 flex items-center space-x-2">
                                <Settings className="h-4 w-4" />
                                <span>Settings</span>
                            </button>
                            <button
                                onClick={handleSignOut}
                                className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50/50 flex items-center space-x-2"
                            >
                                <LogOut className="h-4 w-4" />
                                <span>Sign Out</span>
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </header>
    );
};

export default Header;
